import { Component, computed, inject, signal } from '@angular/core';
import { ProductsService } from '../../../product/products.service';
import { RouterModule } from '@angular/router';
import { auditTime, bufferWhen, filter, map, mergeMap, of, scan, startWith, Subject, switchMap, tap, timer } from 'rxjs';
import { Product, ProductResume } from '../../../product/product.types';
import { LOCALSTORAGE_KEYS } from '../../../shared/constants/databases';
import { LocalStorageService } from '../../../shared/local-storage.service';
import { Location } from '@angular/common';
import { ProductCardV2Component } from '../../../shared/product-card-v2/product-card-v2.component';
import { MatTabsModule } from '@angular/material/tabs';
import { ResolutionService } from '../../../shared/resolution.service';

@Component({
  selector: 'app-welcome',
  imports: [
    RouterModule,
    ProductCardV2Component,
    MatTabsModule
  ],
  templateUrl: './welcome.component.html',
  styleUrl: './welcome.component.scss'
})
export class WelcomeComponent {

  //#region Services

  #productsService = inject(ProductsService);
  #localStorageService = inject(LocalStorageService);
  #location = inject(Location);
  #resolutionService = inject(ResolutionService);

  //#endregion

  //#region Properties

  groupedProducts = signal<{ subcategoryId: number, subcategoryName: string, products: ProductResume[] }[]>([]);

  productDetails = signal<Map<number, Product>>(new Map()); // Cache of product details
  #visibleProducts$ = new Subject<number[]>(); // IDs of products that are visible in the viewport

  isMobile = computed(() => this.#resolutionService.isMobile());

  //#endregion

  //#region Constructors

  constructor() {
    this.getSignupProducts();
  }

  //#endregion

  //#region Methods

  getSignupProducts() {
    this.#productsService
      .getWelcomeProducts()
      .pipe(tap((response: any) => this.groupedProducts.set(response)), tap(() => this.#setupBatchRequests())).subscribe();
  }

  goToShop() {
    this.#localStorageService.set(LOCALSTORAGE_KEYS.NEW_ACCOUNT, true);
    this.#location.replaceState('/shop');
    window.location.reload();
  }

  #setupBatchRequests() {
    const noActivity$ = this.#visibleProducts$.pipe(
      switchMap(() => timer(100).pipe(map(() => true))), // Si no hay nuevos valores en 500ms → inactividad
      startWith(false) // Al inicio hay actividad
    );

    this.#visibleProducts$.pipe(
      bufferWhen(() => noActivity$.pipe(filter(inactive => inactive))), // Espera a que haya inactividad
      filter(productGroups => productGroups.length > 0), // Evita procesar arrays vacíos
      mergeMap(productGroups => productGroups.flat()), // 🔄 Aplana el array → number[]
      scan((acc: number[], productId: number) => [...new Set([...acc, productId])], []), // 📌 Acumula productos únicos
      auditTime(500), // ⏳ Espera estabilidad antes de hacer la petición
      switchMap(productIds => {
        const idsToFetch = productIds.filter(id => !this.productDetails().has(id));
        return idsToFetch.length > 0 ? this.#productsService.getProductsDetailsByIds(idsToFetch) : of({});
      })
    ).subscribe(details => {
      this.productDetails.update(prev => {
        const updatedMap = new Map(prev); // Clonar el Map existente
        Object.entries(details).forEach(([index, detail]) => {
          const productDetail: Product = detail as Product;
          productDetail.isALaCarte = false; // Only for welcome page
          updatedMap.set(Number(productDetail.id), productDetail as Product); // Convertir id a número y agregarlo
        });
        return updatedMap;
      });
    });
  }

  onProductCardReady(productId: number) {
    if (this.productDetails().has(productId)) return;
    else this.#visibleProducts$.next([productId]);
  }

  onProductCardHover(productId: number) {
    if (this.productDetails().has(productId)) return;
    else this.#visibleProducts$.next([productId]);
  }

  //#endregion
}
